<script setup lang="ts">
import {
  EditableArea,
  EditableInput,
  EditablePreview,
  EditableRoot,
  PopoverArrow,
  PopoverClose,
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "reka-ui";
import { computed, ref } from "vue";
import { createActor } from "xstate";
import debounce from "lodash.debounce";
import { useRouter } from "vue-router";

import ConfirmPopover from "@/components/ConfirmPopover.vue";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";
import EvidenceTooltip from "@/components/Tooltip/EvidenceTooltip.vue";

import { useBrandStore } from "@/stores/brand-store";
import { useMessageStore } from "@/stores/message-store";
import { useEvidenceStore } from "@/stores/evidence-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useAnnotationStore } from "@/stores/annotation-store";

import {
  getDefaultEvidenceText,
  getMarkStatus,
  getEvidenceDisplayValue,
  isEqual,
  isHighlightAnnotation,
  isRectangleAnnotation,
  isThisMe,
  isTranscriptAnnotation,
  $can,
} from "@/services/UtilitiesService";
import { BOOKMARK_NAME_MAX_LENGTH, caseEvidenceMachine } from "@/machines/case-evidence-machine";

import type { Evidence } from "@/models/Evidence";
import type { Metadata } from "@/models/Metadata";
import type { MarkEvidenceTheme } from "@/models/MarkEvidenceTheme";

type Category = ReturnType<typeof useEvidenceStore>["availableCategoriesWithEvidences"][number];

const props = defineProps<{
  category: Category;
  brand: string;
  casename: string;
  casefile?: string;
  filepage?: string | number;
  annotation?: string;
  time?: string;
  snippet?: string;
  canBookmarkFile: boolean;
  filteredCategories: Category[];
  themesToShow: "all" | "none" | string[];
}>();

const $router = useRouter();

const editableRootKey = ref<string>();
const activeActor = ref(createActor(caseEvidenceMachine, { input: {} as never }).start());

const activeSnapshot = ref(activeActor.value.getSnapshot());

const newTheme = ref<Record<string, string>>({});

const config = computed(() => {
  return useBrandStore().viewerConfig.evidenceThemes;
});

const payloadItem = computed(() => {
  // Page level annotations are not supported anymore.
  // Keeping it here for legacy purpose
  if (props.filepage) {
    return { type: "page", value: props.filepage.toString() };
  } else if (props.annotation) {
    return { type: "annotation", value: props.annotation };
  } else if (props.time) {
    return { type: "time", value: props.time };
  } else {
    return { type: "page", value: "1" };
  }
});

const annotations = computed(() => {
  return useAnnotationStore().annotationDictionary;
});

const annotationsById = computed(() => {
  const annotations = useAnnotationStore().annotations;
  return annotations.reduce<Record<string, (typeof annotations)[number] | undefined>>(
    (prev, curr) => Object.assign(prev, { [curr.markId]: curr }),
    {},
  );
});

function pageHasAnnotations(fileRef: string, page: number) {
  return (
    annotations.value[fileRef]?.find((a) => {
      if (
        a.parsedMarkData == null ||
        !(isRectangleAnnotation(a.parsedMarkData) || isHighlightAnnotation(a.parsedMarkData))
      )
        return false;

      return a.parsedMarkData.pageNumber === page;
    }) == null
  );
}

function isMarkTranscriptAnnotation(fileRef: string, markId: string) {
  const markData = annotations.value[fileRef]?.find((m) => m.markId === markId)?.parsedMarkData;
  return markData != null && isTranscriptAnnotation(markData);
}

function startEvidenceAction(
  action:
    | { type: "ADD"; category: string }
    | { type: "REMOVE"; evidence: Evidence; all: boolean }
    | { type: "EDIT"; evidence: Evidence }
    | { type: "ACCEPT"; evidence: Evidence }
    | { type: "CHANGE_THEME"; evidence: Evidence; theme: string },
) {
  let actor: typeof activeActor.value | undefined;
  const generalProps = { brand: props.brand, caseReference: props.casename };

  switch (action.type) {
    case "ADD":
      if (!props.annotation || !props.casefile) return;
      actor = createActor(caseEvidenceMachine, {
        input: {
          action: "ADD",
          ...generalProps,
          evidence: { fileReference: props.casefile, theme: action.category, ...payloadItem.value },
        },
      });
      break;
    case "REMOVE":
      actor = createActor(caseEvidenceMachine, {
        input: { action: "REMOVE", ...generalProps, all: action.all, evidence: action.evidence },
      });
      break;
    case "EDIT":
      actor = createActor(caseEvidenceMachine, {
        input: { action: "EDIT", ...generalProps, evidence: action.evidence, isNameTooLong: false },
      });
      break;
    case "ACCEPT":
      actor = createActor(caseEvidenceMachine, {
        input: { action: "ACCEPT", acceptAndAdd: false, ...generalProps, evidence: action.evidence },
      });
      break;
    case "CHANGE_THEME":
      actor = createActor(caseEvidenceMachine, {
        input: { action: "CHANGE_THEME", ...generalProps, evidence: action.evidence, theme: action.theme },
      });
      break;
  }

  if (actor == null) return;
  activeActor.value?.stop();

  actor.subscribe((snapshot) => {
    activeSnapshot.value = snapshot;

    if (snapshot.context.action === "EDIT" && snapshot.matches({ editingEvidence: "error" })) {
      editableRootKey.value = Math.random().toString(36);

      useMessageStore().updateMessage({
        autoHide: true,
        showReload: false,
        title: "Bookmark",
        ...(snapshot.context.isNameTooLong
          ? { type: "warning", message: "The name provided is invalid and will not be saved" }
          : { type: "error", message: "Error renaming bookmark" }),
      });
    }
  });
  activeActor.value = actor.start();
}

const navigate = debounce((item: Evidence) => {
  const params = item.type ? item.type.toLowerCase() + "-" + item.value : "";
  $router.push(`/${props.brand}/${props.casename}/${item.fileReference}/${params}`);
}, 300);

function isItemSelected(item: Evidence): boolean {
  if (!props.casefile) {
    return false;
  }

  const metadata: Metadata | undefined = useMetadataStore().filesByRef[props.casefile];

  if (!metadata || !isThisMe(metadata, item.fileReference)) {
    return false;
  }

  if (item.type === "page") {
    // Are we looking at a time index or an annotation?
    if (props.annotation || props.time) {
      return false;
    }
    // Does the page match? Page 1 will be null, so we need to set it explicitly
    const page: string = String(props.filepage) || "1";
    return String(item.value) === page;
  }
  if (item.type === "annotation") {
    if (!props.annotation) {
      return false;
    }
    return item.value === props.annotation;
  }

  return item.value === props.time;
}

function associatedThemes(item: Evidence) {
  const mark = annotationsById.value[item.value];
  const markStatus = getMarkStatus(mark);

  const filterCriteria = (t: MarkEvidenceTheme) => {
    switch (markStatus) {
      case "accepted":
        return /accepted/i.test(t.status);
      case "candidate":
        return !/rejected/i.test(t.status);
      default:
        return false;
    }
  };

  const themes = annotationsById.value[item.value]?.evidenceThemes?.filter(filterCriteria)?.map((e) => e.evidenceTheme);
  return Array.from(new Set(themes || []));
}

function showBookmarkIcon(category: string): boolean {
  if (
    payloadItem.value.type === "page" ||
    payloadItem.value.type === "time" ||
    !props.casefile ||
    props.themesToShow === "none"
  ) {
    return false;
  }

  if (payloadItem.value.type === "annotation") {
    const a = annotationsById.value[payloadItem.value.value];
    if (a?.parsedMarkData == null || ("type" in a.parsedMarkData && /.*redaction.*/i.test(a.parsedMarkData.type ?? "")))
      return false;
  }

  if (props.themesToShow === "all") {
    return true;
  }

  if (props.themesToShow.find((t) => t === category.toLowerCase())) {
    return true;
  }

  return false;
}

function onEditableStateChange(
  arg: { state: "edit"; evidence: Evidence } | { state: "submit"; name: string } | { state: "cancel" },
) {
  switch (arg.state) {
    case "edit":
      navigate.cancel();
      startEvidenceAction({ type: "EDIT", evidence: arg.evidence });
      break;
    case "cancel":
      activeActor.value.send({ type: "STOP_EDITING" });
      break;
    case "submit":
      activeActor.value.send({ type: "SAVE_ANNOTATION", name: arg.name });
      break;
  }
}
</script>
<template>
  <tr>
    <td v-if="!!config?.highlightColors" style="width: 20px" class="tw-m-2">
      <div
        aria-hidden="true"
        class="tw-ml-1 tw-aspect-square tw-w-full tw-rounded-sm tw-px-0"
        :style="{ 'background-color': config.highlightColors[category.name] ?? 'inherit' }"
      />
    </td>
    <td tabindex="0" class="tw-px-2 tw-leading-6">{{ category.name }} <span class="tw-sr-only">Theme</span></td>
    <td class="tw-text-center tw-text-white">
      <PopoverRoot
        v-if="canBookmarkFile && showBookmarkIcon(props.category.name)"
        :open="
          activeSnapshot.matches({ addEvidence: 'error' }) &&
          activeSnapshot.context.action === 'ADD' &&
          activeSnapshot.context.states?.duplicate
        "
        @update:open="(state) => !state && activeActor.send({ type: 'CONFIRMED_DUPLICATE' })"
      >
        <PopoverTrigger @click.prevent="startEvidenceAction({ type: 'ADD', category: category.name })">
          <i class="far fa-bookmark" />
        </PopoverTrigger>
        <PopoverContent
          :side-offset="3"
          :avoid-collisions="true"
          class="tw-z-[1000] tw-flex tw-select-none tw-flex-col tw-items-end tw-justify-center tw-gap-2 tw-rounded-md tw-border tw-bg-white tw-px-4 tw-py-2 tw-text-xs tw-shadow"
        >
          <p>Bookmark already exists</p>
          <PopoverClose class="tw-btn-md-gray"> OK </PopoverClose>
          <PopoverArrow class="tw-fill-white" />
        </PopoverContent>
      </PopoverRoot>
    </td>
  </tr>
  <template
    v-for="evidence in category.entries"
    :key="`category-${category.name}-item-${evidence.fileReference}-${evidence.value}`"
  >
    <template
      v-for="({ defaultEvidenceText, displayValue, hasOtherAnnotations, isAudio }, idx) in [
        {
          defaultEvidenceText: getDefaultEvidenceText(evidence),
          displayValue: getEvidenceDisplayValue(evidence),
          hasOtherAnnotations: evidence.type === 'page' && pageHasAnnotations(evidence.fileReference, +evidence.value),
          isAudio: evidence.type === 'annotation' && isMarkTranscriptAnnotation(evidence.fileReference, evidence.value),
        },
      ]"
      :key="idx"
    >
      <EvidenceTooltip
        v-if="defaultEvidenceText != null"
        side="top"
        align="center"
        :item="{ ...evidence, displayValue, isAudio, pageHasAnnotations: hasOtherAnnotations }"
      >
        <tr
          :data-selected="isItemSelected(evidence)"
          class="tw-h-full tw-bg-white hover:!tw-bg-[#f3ebff] data-[selected=true]:tw-bg-[#ddd6e8]"
          :class="{
            'tw-bg-orange-300 tw-opacity-75': isEqual('candidate', evidence.status),
          }"
        >
          <td v-if="!!config?.highlightColors" />
          <td class="tw-relative tw-py-1 tw-pl-2.5">
            <div class="tw-grid tw-grid-cols-[max-content_minmax(0,1fr)] tw-items-center tw-gap-1">
              <i
                v-if="hasOtherAnnotations"
                aria-hidden
                class="fas fa-exclamation-circle tw-col-start-1 tw-row-start-1 tw-text-black"
              />
              <EditableRoot
                v-slot="{ isEditing, modelValue }"
                ref="EditableRootRef"
                :key="editableRootKey"
                select-on-focus
                submit-mode="both"
                activation-mode="dblclick"
                class="first:tw-col-span-full"
                :placeholder="defaultEvidenceText"
                :default-value="evidence.annotation"
                :max-length="BOOKMARK_NAME_MAX_LENGTH"
                @interact-outside.prevent
                @update:state="
                  (state) => {
                    if (state === 'edit') onEditableStateChange({ state: 'edit', evidence });
                    if (state === 'cancel') onEditableStateChange({ state: 'cancel' });
                  }
                "
                @submit="(name) => onEditableStateChange({ state: 'submit', name: name ?? '' })"
              >
                <EditableArea class="tw-col-start-2 tw-row-start-1 tw-flex tw-min-h-6 tw-flex-1 tw-items-center">
                  <EditablePreview
                    class="tw-w-full tw-cursor-pointer tw-break-all tw-text-left tw-text-black hover:tw-text-black"
                    :class="!isEditing && 'tw-line-clamp-1'"
                    @click="() => navigate(evidence)"
                  />
                  <EditableInput
                    class="tw-m-0 tw-size-full tw-min-h-[inherit] tw-border-b-2 tw-border-black tw-px-2 tw-py-1 tw-text-black"
                  />
                </EditableArea>
                <span
                  v-if="isEditing && (modelValue ?? '').length > BOOKMARK_NAME_MAX_LENGTH"
                  class="tw-col-span-full tw-row-start-2 tw-text-xs tw-text-red-900"
                >
                  Cannot exceed max of {{ BOOKMARK_NAME_MAX_LENGTH }} characters
                </span>
              </EditableRoot>
            </div>
          </td>
          <td aria-label="Actions" class="tw-text-center">
            <div v-if="$can('annotate')" class="tw-flex tw-h-full tw-justify-evenly tw-px-1">
              <StringTooltip
                v-if="isEqual('candidate', evidence.status)"
                align="end"
                side="bottom"
                item="Accept this candidate mark"
                #="{ item }"
              >
                <button
                  type="button"
                  class="tw-mr-2 tw-w-full tw-py-1 tw-text-black"
                  :aria-label="item"
                  @click="startEvidenceAction({ type: 'ACCEPT', evidence })"
                >
                  <i aria-hidden="true" class="fas fa-check" />
                </button>
              </StringTooltip>
              <ConfirmPopover
                confirm-text="Change theme"
                @confirmed="
                  () => {
                    const theme = newTheme[`theme-${evidence.theme}-${evidence.fileReference}-${evidence.value}`];
                    if (!theme) return;

                    startEvidenceAction({ type: 'CHANGE_THEME', evidence, theme });
                  }
                "
              >
                <template #trigger>
                  <StringTooltip side="bottom" align="end" item="Change theme for this bookmark" #="{ item }">
                    <div class="tw-mr-2 tw-w-full tw-py-1 tw-text-black" :aria-label="item">
                      <i aria-hidden="true" class="fas fa-exchange-alt" />
                    </div>
                  </StringTooltip>
                </template>
                <template #question>
                  <select
                    v-model="newTheme[`theme-${evidence.theme}-${evidence.fileReference}-${evidence.value}`]"
                    class="tw-form-select tw-mb-2"
                  >
                    <option :value="undefined">Select theme</option>
                    <option
                      v-for="theme in filteredCategories.filter((t) => t.name !== category.name)"
                      :key="theme.name"
                    >
                      {{ theme.name }}
                    </option>
                  </select>
                </template>
              </ConfirmPopover>
              <template
                v-if="annotationsById[evidence.value] && !isEqual('user', annotationsById[evidence.value]?.source)"
              >
                <ConfirmPopover
                  v-if="
                    associatedThemes(evidence).length > 1 ||
                    getMarkStatus(annotationsById[evidence.value]) === 'accepted'
                  "
                  advanced
                >
                  <template #trigger>
                    <StringTooltip side="bottom" align="end" item="Mark as inaccurate" #="{ item }">
                      <div class="tw-w-full tw-py-1 tw-text-black" :aria-label="item">
                        <i aria-hidden="true" class="fas fa-times-circle" />
                      </div>
                    </StringTooltip>
                  </template>
                  <template #question>
                    <p>Are you sure you want to reject this theme?</p>
                    <p v-if="associatedThemes(evidence).length > 1">Associate themes:</p>
                    <ul v-if="associatedThemes(evidence).length > 1">
                      <li v-for="theme in associatedThemes(evidence)" :key="theme">
                        {{ theme }}
                      </li>
                    </ul>
                    <div class="tw-mt-3 tw-flex tw-justify-end">
                      <PopoverClose class="tw-btn-md-white tw-b-r tw-mr-2">Cancel</PopoverClose>
                      <button
                        type="button"
                        class="tw-btn-md-gray tw-b-r"
                        @click="startEvidenceAction({ type: 'REMOVE', evidence, all: false })"
                      >
                        Reject
                      </button>
                      <button
                        v-if="associatedThemes(evidence).length > 1"
                        type="button"
                        class="tw-btn-md-gray tw-b-r"
                        @click="startEvidenceAction({ type: 'REMOVE', evidence, all: true })"
                      >
                        Reject all themes
                      </button>
                    </div>
                  </template>
                </ConfirmPopover>
                <StringTooltip v-else side="bottom" align="end" item="Mark as inaccurate" #="{ item }">
                  <button
                    :id="`delete-${evidence.theme}-${evidence.fileReference}-${evidence.value}`"
                    type="button"
                    class="tw-w-full tw-py-1 tw-text-black"
                    :aria-label="item"
                    @click="startEvidenceAction({ type: 'REMOVE', evidence, all: false })"
                  >
                    <i aria-hidden="true" class="fas fa-times-circle" />
                  </button>
                </StringTooltip>
              </template>
              <ConfirmPopover
                v-else
                confirm-text="Delete"
                @confirmed="() => startEvidenceAction({ type: 'REMOVE', evidence, all: false })"
              >
                <template #trigger>
                  <StringTooltip side="bottom" align="end" item="Delete this bookmark" #="{ item }">
                    <div class="tw-w-full tw-py-1 tw-text-black" :aria-label="item">
                      <i aria-hidden="true" class="fas fa-times-circle" />
                    </div>
                  </StringTooltip>
                </template>
                <template #question>Are you sure you want to delete this bookmark?</template>
              </ConfirmPopover>
            </div>
          </td>
        </tr>
      </EvidenceTooltip>
    </template>
  </template>
</template>
